/*!
 * HoloFrame SiteVideo — shared autoplay / lazy / pause-offscreen controller.
 * Modes via data-holo-video-mode:
 *   priority      — hero / LCP; source attached immediately
 *   lazyAutoplay  — attach source near viewport, pause when far offscreen
 *   userControlled — leave alone (native controls OK)
 *
 * Callers: theme.liquid (script tag), holo-ui.js (delegates), holo-finished-clip /
 * holo-loop-video markup (data attributes), pets lazy videos (data-holo-lazy-video).
 * API: window.__holoSiteVideo { scan, play, pause, register }; __holoPlayLoopVideos.
 * No server schema — client-only DOM/data-* contract.
 * User request: shared AutoplayVideo/SiteVideo with muted autoplay, lazy load,
 * pause offscreen, poster fallback, no giant play buttons.
 */
(function () {
  if (window.__HOLO_SITE_VIDEO__) return;
  window.__HOLO_SITE_VIDEO__ = true;

  var SELECTOR =
    'video[data-holo-video-mode], video[data-holo-loop-video], video[data-holo-lazy-video], ' +
    '.holo-compare__pane video, video.holo-compare__video, video.holo-media--video, ' +
    '.hf-hero__stage video, .hf-how__float--holo video, .holo-clip video';

  var ROOT_MARGIN_LOAD = '480px 0px';
  var ROOT_MARGIN_PLAY = '80px 0px';
  var MOBILE_MQ = '(max-width: 749px)';

  var reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var observed = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var loadObserver = null;
  var playObserver = null;

  function modeOf(video) {
    var mode = (video.getAttribute('data-holo-video-mode') || '').trim();
    if (mode === 'priority' || mode === 'lazyAutoplay' || mode === 'userControlled') return mode;
    if (video.hasAttribute('data-holo-lazy-video') || video.hasAttribute('data-src') || video.querySelector('source[data-src]')) {
      return 'lazyAutoplay';
    }
    if (video.closest('.hf-hero__stage, .holo-home-hero__media, .holo-pets-hero__media')) {
      return 'priority';
    }
    return 'lazyAutoplay';
  }

  function isDisplayed(el) {
    if (!el || !el.isConnected) return false;
    var node = el;
    while (node && node.nodeType === 1) {
      if (node.hasAttribute('hidden')) return false;
      var style = window.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden') return false;
      node = node.parentElement;
    }
    return true;
  }

  function matchesBreakpoint(video) {
    var gate = (video.getAttribute('data-holo-viewport') || '').trim();
    if (gate === 'desktop' || gate === 'mobile') {
      var mobileVp = window.matchMedia(MOBILE_MQ).matches;
      if (gate === 'desktop' && mobileVp) return false;
      if (gate === 'mobile' && !mobileVp) return false;
      return true;
    }
    var media = video.closest('.holo-home-hero__media, .holo-pets-hero__media');
    if (!media) return true;
    var isMobile = window.matchMedia(MOBILE_MQ).matches;
    if (media.classList.contains('holo-home-hero__media--desktop') && isMobile) return false;
    if (media.classList.contains('holo-home-hero__media--mobile') && !isMobile) return false;
    return true;
  }

  function shouldDrive(video) {
    if (!video || modeOf(video) === 'userControlled') return false;
    if (document.hidden) return false;
    if (video.closest('[hidden]')) return false;
    if (!isDisplayed(video)) return false;
    if (!matchesBreakpoint(video)) return false;
    return true;
  }

  function muteForAutoplay(video) {
    video.muted = true;
    video.defaultMuted = true;
    try {
      video.volume = 0;
    } catch (e) {}
    video.setAttribute('muted', '');
    video.playsInline = true;
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.controls = false;
    video.removeAttribute('controls');
    video.disablePictureInPicture = true;
    video.setAttribute('disablepictureinpicture', '');
    video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
    video.loop = true;
    video.autoplay = true;
  }

  function pickMp4Url(video) {
    var dataSrc = video.getAttribute('data-src') || '';
    if (dataSrc) return dataSrc;
    var probe = video.previousElementSibling;
    var probeUrls =
      probe && probe.tagName === 'TEMPLATE'
        ? Array.prototype.slice
            .call(probe.content.querySelectorAll('source, video'))
            .map(function (node) {
              return node.getAttribute('src') || node.src || node.getAttribute('data-src') || '';
            })
        : [];
    var urls = [
      video.getAttribute('src'),
      video.currentSrc,
    ]
      .concat(
        Array.prototype.slice.call(video.querySelectorAll('source')).map(function (source) {
          return source.getAttribute('data-src') || source.getAttribute('src') || '';
        })
      )
      .concat(probeUrls)
      .filter(Boolean);
    return (
      urls.find(function (url) {
        return /\.mp4(\?|#|$)/i.test(url) && !/\.m3u8/i.test(url);
      }) || ''
    );
  }

  function setState(video, state) {
    video.dataset.holoVideoState = state;
    var root = video.closest('.holo-clip, .hf-hero__stage, .hf-how__float-media, .holo-media-frame') || video.parentElement;
    if (root && root.classList) {
      root.classList.toggle('is-holo-video-ready', state === 'ready' || state === 'playing');
      root.classList.toggle('is-holo-video-playing', state === 'playing');
      root.classList.toggle('is-holo-video-blocked', state === 'blocked' || state === 'error');
    }
  }

  function showPosterFallback(video) {
    setState(video, 'blocked');
    video.classList.add('is-holo-poster-fallback');
    video.controls = false;
    video.removeAttribute('controls');
  }

  function attachSource(video) {
    if (video.dataset.holoSrcReady === 'true') return true;
    if (!matchesBreakpoint(video)) return false;

    var sourceEl = video.querySelector('source[data-src]');
    if (sourceEl) {
      sourceEl.src = sourceEl.getAttribute('data-src');
      sourceEl.removeAttribute('data-src');
      video.dataset.holoSrcReady = 'true';
      video.preload = modeOf(video) === 'priority' ? 'auto' : 'metadata';
      muteForAutoplay(video);
      video.load();
      setState(video, 'loading');
      return true;
    }

    var mp4 = pickMp4Url(video);
    if (!mp4) return false;

    var current = video.getAttribute('src') || '';
    if (!current || current !== mp4) {
      Array.prototype.forEach.call(video.querySelectorAll('source'), function (source) {
        source.remove();
      });
      video.setAttribute('src', mp4);
      video.src = mp4;
    }
    video.removeAttribute('data-src');
    video.dataset.holoSrcReady = 'true';
    video.preload = modeOf(video) === 'priority' ? 'auto' : 'metadata';
    muteForAutoplay(video);
    if (!current) video.load();
    setState(video, 'loading');
    return true;
  }

  function tryPlay(video) {
    if (reduceMotion) {
      showPosterFallback(video);
      if (!video.paused) video.pause();
      return;
    }
    if (!shouldDrive(video)) {
      if (video.dataset.holoSrcReady === 'true' && !video.paused) video.pause();
      return;
    }
    if (!attachSource(video)) return;

    muteForAutoplay(video);
    video.dataset.holoWantPlay = '1';

    if (!video.paused && !video.ended) {
      setState(video, 'playing');
      video.classList.remove('is-holo-poster-fallback');
      return;
    }

    var attempt = video.play();
    if (attempt && typeof attempt.then === 'function') {
      attempt
        .then(function () {
          setState(video, 'playing');
          video.classList.remove('is-holo-poster-fallback');
        })
        .catch(function () {
          showPosterFallback(video);
        });
    }
  }

  function pauseVideo(video) {
    video.dataset.holoWantPlay = '0';
    if (!video.paused) video.pause();
    if (video.dataset.holoVideoState === 'playing') setState(video, 'ready');
  }

  function armVideo(video) {
    if (video.dataset.holoSiteArmed === '1') return;
    video.dataset.holoSiteArmed = '1';
    muteForAutoplay(video);

    if (modeOf(video) === 'userControlled') return;

    video.addEventListener('canplay', function () {
      if (video.dataset.holoWantPlay === '1') tryPlay(video);
    });
    video.addEventListener('loadeddata', function () {
      if (video.dataset.holoWantPlay === '1') tryPlay(video);
      else setState(video, 'ready');
    });
    video.addEventListener('playing', function () {
      setState(video, 'playing');
      video.classList.remove('is-holo-poster-fallback');
    });
    video.addEventListener('error', function () {
      showPosterFallback(video);
    });
    video.addEventListener('pause', function () {
      if (video.dataset.holoWantPlay !== '1') return;
      if (!shouldDrive(video)) return;
      window.setTimeout(function () {
        if (video.dataset.holoWantPlay === '1' && video.paused) tryPlay(video);
      }, 120);
    });
  }

  function ensureObservers() {
    if (!('IntersectionObserver' in window)) return false;
    if (!loadObserver) {
      loadObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (!entry.isIntersecting) return;
            if (!matchesBreakpoint(video) || !isDisplayed(video)) return;
            attachSource(video);
            loadObserver.unobserve(video);
          });
        },
        { root: null, rootMargin: ROOT_MARGIN_LOAD, threshold: 0.01 }
      );
    }
    if (!playObserver) {
      playObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (!shouldDrive(video)) {
              pauseVideo(video);
              return;
            }
            if (entry.isIntersecting && entry.intersectionRatio > 0) {
              tryPlay(video);
            } else {
              pauseVideo(video);
            }
          });
        },
        { root: null, rootMargin: ROOT_MARGIN_PLAY, threshold: [0, 0.05, 0.2] }
      );
    }
    return true;
  }

  function register(video) {
    if (!video || (observed && observed.has(video))) return;
    if (observed) observed.add(video);

    var mode = modeOf(video);
    if (mode === 'userControlled') return;

    armVideo(video);
    setState(video, video.dataset.holoVideoState || 'initial');

    if (reduceMotion) {
      showPosterFallback(video);
      return;
    }

    if (mode === 'priority') {
      if (matchesBreakpoint(video)) {
        video.preload = 'auto';
        attachSource(video);
        tryPlay(video);
      }
      if (ensureObservers()) playObserver.observe(video);
      return;
    }

    if (!video.getAttribute('data-src') && !video.querySelector('source[data-src]')) {
      var existing = video.getAttribute('src') || pickMp4Url(video);
      if (existing && video.dataset.holoSrcReady !== 'true') {
        video.setAttribute('data-src', existing);
        video.removeAttribute('src');
        video.removeAttribute('autoplay');
        video.preload = 'none';
        try {
          video.load();
        } catch (e) {}
      }
    } else {
      video.preload = 'none';
    }

    if (!ensureObservers()) {
      attachSource(video);
      tryPlay(video);
      return;
    }
    loadObserver.observe(video);
    playObserver.observe(video);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(SELECTOR), register);
  }

  function onBreakpointChange() {
    Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), function (video) {
      if (modeOf(video) === 'userControlled') return;
      if (!matchesBreakpoint(video)) {
        pauseVideo(video);
        return;
      }
      if (modeOf(video) === 'priority') {
        attachSource(video);
        tryPlay(video);
        return;
      }
      if (video.dataset.holoSrcReady === 'true') {
        tryPlay(video);
      }
    });
  }

  function boot() {
    scan(document);

    if (!boot.bound) {
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) {
          Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), pauseVideo);
        } else {
          scan(document);
          Array.prototype.forEach.call(document.querySelectorAll(SELECTOR), function (video) {
            if (modeOf(video) === 'priority' || video.dataset.holoWantPlay === '1') tryPlay(video);
          });
        }
      });
      window.addEventListener('pageshow', function () {
        scan(document);
      });
      if (window.matchMedia) {
        window.matchMedia(MOBILE_MQ).addEventListener('change', onBreakpointChange);
      }
      document.addEventListener('shopify:section:load', function (event) {
        scan(event && event.target ? event.target : document);
      });
      boot.bound = true;
    }
  }

  window.__holoSiteVideo = {
    scan: scan,
    play: tryPlay,
    pause: pauseVideo,
    register: register,
  };
  window.__holoPlayLoopVideos = function () {
    scan(document);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
